import { evolveBibleForSequel, evolveLedgerForSequel } from './golden/gemini.agents';
import { createGoldenGenerateBinding } from './adapters/goldenGenerateBinding';
import type {
  OriginalEngineAITransport,
  OriginalEngineSequelRepository,
  OriginalEngineSequelRunResult,
} from './types';

export interface OriginalEngineCompatSequelRunnerDeps {
  transport: OriginalEngineAITransport;
  repository: OriginalEngineSequelRepository;
}

export interface OriginalEngineCompatSequelRunInput {
  sourceProjectId: string;
  newProjectId?: string;
}

function createProjectId(): string {
  return Math.random().toString(36).substring(2, 15);
}

export class OriginalEngineCompatSequelRunner {
  constructor(private readonly deps: OriginalEngineCompatSequelRunnerDeps) {}

  async createSequel(input: OriginalEngineCompatSequelRunInput): Promise<OriginalEngineSequelRunResult> {
    try {
      const complete = await this.deps.repository.verifySourceVolumeComplete(input.sourceProjectId);
      if (!complete) {
        return {
          ok: false,
          code: 'SOURCE_VOLUME_INCOMPLETE',
          message: 'Source volume must have at least one committed chapter before creating a sequel.',
        };
      }

      const source = await this.deps.repository.loadProject(input.sourceProjectId);
      createGoldenGenerateBinding(this.deps.transport, {
        projectId: input.sourceProjectId,
        chapterNumber: source.currentChapterNumber,
      });

      const evolvedLedger = await evolveLedgerForSequel(source.ledger, source.storyBible);
      const evolvedBible = await evolveBibleForSequel(source.storyBible);
      const seriesId = source.storyBible.seriesId || input.sourceProjectId;
      evolvedBible.seriesId = seriesId;

      const newProjectId = input.newProjectId || createProjectId();
      await this.deps.repository.createSequelWorkspace({
        newProjectId,
        seriesId,
        previousVolumeId: input.sourceProjectId,
        ledger: evolvedLedger,
        storyBible: evolvedBible,
      });

      return {
        ok: true,
        newProjectId,
        seriesId,
        previousVolumeId: input.sourceProjectId,
        volume: evolvedBible.volume,
        ledger: evolvedLedger,
        storyBible: evolvedBible,
      };
    } catch (error: any) {
      return {
        ok: false,
        code: error?.code || error?.name || 'SEQUEL_FAILED',
        message: error?.message || 'Sequel workspace creation failed',
      };
    }
  }
}
